/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Form, Table, Icon, Dropdown, Popup, Accordion } from "semantic-ui-react";
import * as _ from "lodash";
import { getDuplicateEscalatorParameterBindings } from "../../utils/pdApiUtils";

type EscalatorParameterBindingsProps = {
  domainId: number;
  code: number;
  bindings: Api.EscalatorParameterBinding[];
  escalatorVersions: Api.EscalatorVersions;
  domains: Api.Domains;
  handleBindingsChanged: (bindings: Api.EscalatorParameterBinding[]) => void;
  permissions: boolean;
  storedbindings: Api.EscalatorParameter;
  editedbindings: Api.EscalatorParameter;
};

type EscalatorParameterBindingsState = {
  isOpen: boolean;
};

/** EscalatorParameterBindings component */
class EscalatorParameterBindings extends React.Component<
EscalatorParameterBindingsProps,
EscalatorParameterBindingsState
> {
  state = { isOpen: false };

  render(): JSX.Element {
    // eslint-disable-next-line
    const { bindings, escalatorVersions, domains, permissions, storedbindings, editedbindings, domainId } = this.props;
    const { isOpen } = this.state;
    let versionOptions: any[] = [];
    const duplicateBindings = getDuplicateEscalatorParameterBindings(bindings, escalatorVersions, domainId);
    const extendedBindings = bindings
      .map((binding, index) => {
        if (duplicateBindings.find(duplicateBinding => duplicateBinding === index) !== undefined) {
          return { ...binding, isDuplicate: true };
        } else {
          return { ...binding, isDuplicate: false };
        }
      });

    editedbindings.escalatorParameterBindings.forEach((editedBinding: Api.EscalatorParameterBinding) => {
      if (editedBinding.isDirty) {
        delete editedBinding.isDirty;
      }
    });

    // Creates escalator's version dropdown options
    if (isOpen) {
      versionOptions = _.values(escalatorVersions)
        .filter(escalatorVersion => escalatorVersion.domain.id === this.props.domainId)
        .map(escalatorVersion => ({
          key: escalatorVersion.id,
          text: `${escalatorVersion.number}.${
            domains[escalatorVersion.domain.id].enumName
          }`,
          value: escalatorVersion.id
        }));
      versionOptions = _.sortBy(versionOptions, ["text"]).reverse();
    }

    let showMandatory = true;
    
    if (storedbindings) {
      showMandatory = !_.isEqual(storedbindings, editedbindings);
    }

    return (
      <Form.Group id="escalatorParameterBindings" grouped={true}>
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title
              active={isOpen}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onClick={() => this.setState(prevState => ({ isOpen: !isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Parameter bindings</b>
              </label>
              {duplicateBindings.length >0 && <p className="errorMessagePadding">
              Bindings should not be empty or duplicated.</p>}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Name</Table.HeaderCell>
                    {!permissions && <Table.HeaderCell>
                      <Popup
                        trigger={
                          <Icon
                            id="addEscalatorBinding"
                            className="color-green"
                            link={true}
                            name="add"
                            onClick={this.handleAddBinding}
                          />
                        }
                        content={"Add binding"}
                      />
                    </Table.HeaderCell>}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {extendedBindings.map(
                    (binding, index): JSX.Element => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <div className="flex-row">
                            {binding.isDirty && showMandatory ? "*" : ""}
                            <Dropdown
                              id={`escalatorBinding-${index}`}
                              compact={true}
                              fluid={true}
                              selection={true}
                              error={binding.isDuplicate}
                              value={binding.escalatorVersion.id}
                              options={versionOptions}
                              onChange={(e: any, data: any) =>
                                this.handlEscalatorVersionChange(data.value, index)
                              }
                              disabled={permissions}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {escalatorVersions[binding.escalatorVersion.id] && (
                            <Popup
                              trigger={
                                <Icon className="color-blue" link={false} name="info circle" />
                              }
                              content={escalatorVersions[binding.escalatorVersion.id].versionName}
                            />
                          )}
                        </Table.Cell>
                        {!permissions && <Table.Cell>
                          <Popup
                            trigger={
                              <Icon
                                className="color-red"
                                link={true}
                                name="remove"
                                onClick={() => this.handleRemoveBinding(index)}
                              />
                            }
                            content={"Remove binding"}
                          />
                        </Table.Cell>}
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Add new binding handler
  private handleAddBinding = (): void => {
    const { code, bindings, handleBindingsChanged } = this.props;
    const newBinding: Api.EscalatorParameterBinding = {
      escalatorParameterCode: code,
      escalatorVersion: {
        id: -1,
        url: ""
      },
      isDirty: true
    };
    handleBindingsChanged([newBinding, ...bindings]);
  };

  // Remove binding handler
  private handleRemoveBinding = (removedIndex: number): void => {
    const { bindings, handleBindingsChanged } = this.props;
    const modifiedBindings = bindings.filter((value, index) => index !== removedIndex);
    handleBindingsChanged(modifiedBindings);
  };

  // Change version handler
  private handlEscalatorVersionChange = (versionId: number, changedIndex: number): void => {
    const { bindings, escalatorVersions, handleBindingsChanged } = this.props;
    const escalatorVersion = escalatorVersions[versionId];
    if (escalatorVersion) {
      const editedBindings = bindings.map((binding, index) => {
        if (changedIndex === index) {
          return {
            ...binding,
            escalatorVersion: { id: escalatorVersion.id, url: escalatorVersion.url },
            isDirty: true
          };
        } else {
          return binding;
        }
      });
      handleBindingsChanged(editedBindings);
    }
  };
}

export default EscalatorParameterBindings;
